import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Loader2, Check, X, RefreshCw, MessageSquare, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

/**
 * PendingCheckinsCard — surfaces patients who just scanned the doctor's
 * "Clinic Check-In QR" and need a single tap from the doctor to finalise.
 *
 * Auto-polls every 12 s so a fresh check-in appears inside ~10 s of the
 * scan. Doctor adds channel + (optionally amends) symptom + tap Save —
 * row moves into the regular Patient Log below. Reject deletes the row
 * and the dangling TQS survey it spawned (for stray scans).
 *
 * Hidden when nothing is pending — keeps the dashboard quiet by default.
 */

const CHANNEL_OPTS = [
  { value: 'walkIn', label: 'Walk-in' },
  { value: 'PolyClinic', label: 'Poly / Clinic walk-in' },
  { value: 'Hospital', label: 'Hospital walk-in' },
  { value: 'ReferredByDr', label: 'Referred by Doctor' },
  { value: 'ReferredByHospital', label: 'Referred by Hospital' },
  { value: 'ReferredByPatient', label: 'Referred by Patient' },
  { value: 'myRefNetwork', label: 'My referral network' },
];

// Render a server-stamped UTC ISO string as a short relative time
// ("just now", "3 min ago", "2 hr ago"). Uses Date math so it's correct
// across timezones (avoids the old UTC-vs-IST 5h30m skew).
function timeAgo(iso) {
  if (!iso) return 'just now';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'just now';
    const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    return `${Math.floor(hrs / 24)} d ago`;
  } catch {
    return 'just now';
  }
}

export default function PendingCheckinsCard({ onFinalized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Editable in-place fields per row, keyed by visit id.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  // Which check-in row is expanded (tap a green row to open Accept/Reject).
  const [openId, setOpenId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/checkins/pending');
      setItems(Array.isArray(data?.checkins) ? data.checkins : []);
    } catch { /* silent — the regular log still shows */ }
  }, []);

  // Initial + tab-focus + 12 s auto-poll.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPending().finally(() => { if (!cancelled) setLoading(false); });
    const id = setInterval(() => { if (!document.hidden) fetchPending(); }, 12_000);
    const onVis = () => { if (!document.hidden) fetchPending(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchPending]);

  const editFor = (id) => edits[id] || {};
  const setField = (id, key, val) => setEdits(e => ({ ...e, [id]: { ...e[id], [key]: val } }));

  const finalize = async (item) => {
    setSaving(s => ({ ...s, [item.id]: true }));
    try {
      const e = editFor(item.id);
      const amt = Math.max(0, Math.min(99999, parseInt(e.amount_paid, 10) || 0));
      await api.post(`/dashboard/checkins/${item.id}/finalize`, {
        primary_symptom: e.primary_symptom ?? item.primary_symptom ?? '',
        channel: e.channel || 'walkIn',
        visit_type: 'consult',
        notes: e.notes || '',
        amount_paid: amt,
        payment_mode: e.payment_mode || 'cash',
        service_type: e.service_type || 'consult',
      });
      setItems(prev => prev.filter(x => x.id !== item.id));
      setEdits(prev => { const { [item.id]: _drop, ...rest } = prev; return rest; });
      onFinalized && onFinalized();
    } catch { /* keep row, surface a toast in future */ }
    finally { setSaving(s => ({ ...s, [item.id]: false })); }
  };

  const reject = async (item) => {
    if (!window.confirm('Mark this check-in as a stray scan? The record stays in your patient log (cannot be deleted), but stops appearing as pending and no feedback SMS goes out.')) return;
    setSaving(s => ({ ...s, [item.id]: true }));
    try {
      await api.delete(`/dashboard/checkins/${item.id}`);
      setItems(prev => prev.filter(x => x.id !== item.id));
    } finally {
      setSaving(s => ({ ...s, [item.id]: false }));
    }
  };

  // Accept the patient's re-submitted fuller details (auto-merges).
  const acceptUpdate = async (item) => {
    setSaving(s => ({ ...s, [item.id]: true }));
    try {
      await api.post(`/dashboard/checkins/${item.id}/accept-update`);
      // Refresh so the row shows the merged name immediately.
      await fetchPending();
    } finally {
      setSaving(s => ({ ...s, [item.id]: false }));
    }
  };

  // Discard the proposed update — original row stays untouched.
  const rejectUpdate = async (item) => {
    setSaving(s => ({ ...s, [item.id]: true }));
    try {
      await api.post(`/dashboard/checkins/${item.id}/reject-update`);
      await fetchPending();
    } finally {
      setSaving(s => ({ ...s, [item.id]: false }));
    }
  };

  // Nothing to show — render an inline tip the FIRST time, then nothing.
  if (!loading && items.length === 0) return null;

  return (
    <div data-testid="pending-checkins-card">
      <div className="flex items-center justify-between mb-2 px-0.5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
          New check-ins · in clinic now{items.length ? ` · ${items.length}` : ''}
        </p>
        <button
          onClick={() => fetchPending()}
          className="p-1 rounded-full text-emerald-700 hover:bg-emerald-100"
          aria-label="Refresh"
          data-testid="pending-checkins-refresh"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      <div className="space-y-2">
        {loading && items.length === 0 && (
          <div className="px-2 py-5 text-emerald-700/60 text-xs flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        )}
        {items.map(item => {
          const e = editFor(item.id);
          const sym = e.primary_symptom ?? item.primary_symptom ?? '';
          const ch = e.channel ?? 'walkIn';
          const isOpen = openId === item.id;
          const clinic3 = (item.location_name || 'Clinic').trim().slice(0, 3).toUpperCase();
          return (
            <div key={item.id} data-testid={`pending-checkin-row-${item.id}`}>
              {/* Collapsed green summary row — one per check-in */}
              <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className="w-full flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100/70 p-3 text-left transition-colors"
                aria-expanded={isOpen}
                data-testid={`pending-checkin-toggle-${item.id}`}
              >
                <span className="w-9 h-9 rounded-full bg-emerald-500 grid place-items-center text-white shrink-0"><Sparkles size={15} /></span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5 font-semibold text-emerald-900 text-sm" data-testid={`pending-checkin-name-${item.id}`}>
                    <span className="truncate">{item.patient_name}</span>
                    {item.patient_gender ? <span className="text-[10px] uppercase tracking-wider text-emerald-700/80 font-medium shrink-0">· {item.patient_gender.charAt(0).toUpperCase()}</span> : null}
                    {item.proposed_update && (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 border border-amber-400 text-amber-700 text-[10px] font-bold shrink-0" title="Patient resubmitted fuller details — open to review." data-testid={`pending-checkin-proposed-update-badge-${item.id}`}>?</span>
                    )}
                  </span>
                  <span className="block text-[11.5px] text-emerald-700 truncate mt-0.5">
                    Scanned clinic QR · {clinic3} · {timeAgo(item.created_at)}
                  </span>
                </span>
                <ChevronRight size={16} className={`text-emerald-600 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>

              {/* Expanded editor — opens on tap; Accept saves into the patient database */}
              {isOpen && (
                <div className="rounded-xl border border-emerald-200 bg-white p-3 mt-1.5 space-y-2.5" data-testid={`pending-checkin-editor-${item.id}`}>
                  {item.proposed_update && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] leading-snug text-amber-900" data-testid={`pending-checkin-proposed-update-panel-${item.id}`}>
                      <p className="font-semibold mb-0.5">Fuller details just submitted:</p>
                      {Object.entries(item.proposed_update).map(([k, v]) => (
                        <div key={k} className="flex gap-1.5 truncate"><span className="text-amber-700/70">{k.replace(/_/g, ' ')}:</span> <span className="truncate">{String(v)}</span></div>
                      ))}
                      <div className="mt-1.5 flex gap-1.5">
                        <button onClick={() => acceptUpdate(item)} disabled={saving[item.id]} className="flex-1 px-2 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-semibold disabled:opacity-60" data-testid={`pending-checkin-accept-update-${item.id}`}>Accept update</button>
                        <button onClick={() => rejectUpdate(item)} disabled={saving[item.id]} className="px-2 py-1 rounded-md bg-white border border-amber-300 text-amber-800 text-[11px] font-semibold hover:bg-amber-50 disabled:opacity-60" data-testid={`pending-checkin-reject-update-${item.id}`}>Keep current</button>
                      </div>
                    </div>
                  )}
                  <input type="text" value={sym} onChange={(ev) => setField(item.id, 'primary_symptom', ev.target.value)} placeholder="Symptom / reason" className="w-full text-sm rounded-lg border border-emerald-200 px-2.5 py-2 bg-white focus:ring-2 focus:ring-emerald-500/30 outline-none" data-testid={`pending-checkin-symptom-${item.id}`} />
                  <select value={ch} onChange={(ev) => setField(item.id, 'channel', ev.target.value)} className="w-full text-sm rounded-lg border border-emerald-200 px-2.5 py-2 bg-white focus:ring-2 focus:ring-emerald-500/30 outline-none" data-testid={`pending-checkin-channel-${item.id}`}>
                    {CHANNEL_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={e.service_type || 'consult'} onChange={(ev) => setField(item.id, 'service_type', ev.target.value)} className="text-[12px] rounded-lg border border-emerald-200 px-2 py-2 bg-white" data-testid={`pending-checkin-service-${item.id}`}>
                      <option value="consult">Consult</option>
                      <option value="procedure">Procedure</option>
                      <option value="surgery">Surgery</option>
                    </select>
                    <select value={e.payment_mode || 'cash'} onChange={(ev) => setField(item.id, 'payment_mode', ev.target.value)} className="text-[12px] rounded-lg border border-emerald-200 px-2 py-2 bg-white" data-testid={`pending-checkin-mode-${item.id}`}>
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[12px] pointer-events-none">₹</span>
                      <input type="number" min="0" max="99999" step="1" inputMode="numeric" pattern="[0-9]*" value={e.amount_paid ?? ''} onChange={(ev) => setField(item.id, 'amount_paid', ev.target.value.replace(/[^0-9]/g, '').slice(0, 5))} placeholder="Amount" className="w-full text-[12px] rounded-lg border border-emerald-200 pl-5 pr-2 py-2 bg-white focus:ring-2 focus:ring-emerald-500/30 outline-none" data-testid={`pending-checkin-amount-${item.id}`} />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-0.5">
                    <button onClick={() => finalize(item)} disabled={saving[item.id]} className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold flex items-center justify-center gap-1.5" data-testid={`pending-checkin-save-${item.id}`}>
                      {saving[item.id] ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} />} Accept
                    </button>
                    <button onClick={() => reject(item)} disabled={saving[item.id]} className="px-4 h-11 rounded-xl bg-white border border-rose-200 text-rose-600 text-sm font-bold hover:bg-rose-50 flex items-center justify-center gap-1.5" data-testid={`pending-checkin-reject-${item.id}`}>
                      <X size={16} /> Reject
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">Accept opens the patient record and saves it to your patient database.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-2 px-0.5 text-[10px] text-emerald-800/70 flex items-center gap-1.5">
        <MessageSquare size={10} />
        TQS feedback link auto-SMS&rsquo;d to each patient · Q4 / outcome rating opens day 7.
      </p>
    </div>
  );
}
